import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Switch,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Alert,
  Typography,
  Avatar,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NoticeboardButton } from "../shared";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import ProfileImageSelectorModal from "../ProfileImageSelectorModal";
import ImageCropperModal from "../Advertisements/ImageCropperModal";
import { lightTheme } from "../../styles/theme";
import {
  ORGANIZATION_BANNER_ASPECT_RATIO,
  preprocessOrgBannerDataUrl,
} from "../../utils/imageUpload";
import {
  CIRCLE_DESCRIPTION_MAX_LENGTH,
  CIRCLE_LEARNING_POINT_MAX,
  CIRCLE_LEARNING_POINT_OPTIONS,
  DESCRIPTION_HELPER_TEXT,
  DESCRIPTION_MAX_LENGTH_MESSAGE,
  DESCRIPTION_REQUIRED_MESSAGE,
  LEARNING_POINTS_HELPER_TEXT,
  LEARNING_POINTS_REQUIRED_MESSAGE,
} from "./learningPointOptions";
import type { EditCircleFormFooterState } from "./circleFormFooterState";

/** In-form preview only (modal/page); crop and saved banner stay 16:9 */
const BANNER_PREVIEW_ASPECT =
  ORGANIZATION_BANNER_ASPECT_RATIO * 1.4 * 1.25;

interface Circle {
  circleId: string;
  name: string;
  description: string;
  isPublic: boolean;
  memberCount: number;
  createdAt: string;
  createdBy: string;
  isSubCircle: boolean;
  parentCircle?: string;
  circleApproved: boolean;
  logo: string;
  type: string;
  /** Effective banner URL (includes main-org fallback when not set on the subcircle row). */
  orgBanner?: string;
  learningPoints?: string[];
}

export const EDIT_CIRCLE_FORM_ID = "edit-circle-form";
export type { EditCircleFormFooterState };

interface EditCircleFormProps {
  circle: Circle;
  onCircleUpdated: (updatedCircle: Circle) => void;
  onCancel: () => void;
  /** Full-page hub (wider layout, Cancel + Update). Modal uses compact width. */
  layout?: "modal" | "page";
  /** Puts primary submit in ActionModal footer on small screens. */
  anchorSubmitToFooter?: boolean;
  onFooterStateChange?: (state: EditCircleFormFooterState) => void;
}

const EditCircleForm: React.FC<EditCircleFormProps> = ({
  circle,
  onCircleUpdated,
  onCancel,
  layout = "modal",
  anchorSubmitToFooter = false,
  onFooterStateChange,
}) => {
  const { user, token } = useAuth();
  const theme = useTheme();
  const bannerFileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: circle.name,
    description: circle.description,
    isPublic: circle.isPublic,
    logo: circle.logo,
    orgBanner: circle.orgBanner ?? "",
    learningPoints: Array.isArray(circle.learningPoints)
      ? circle.learningPoints.slice(0, CIRCLE_LEARNING_POINT_MAX)
      : [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  const [isBannerCropperOpen, setIsBannerCropperOpen] = useState(false);
  const [bannerImageToCrop, setBannerImageToCrop] = useState<string | null>(
    null
  );
  const [visibilityConfirmOpen, setVisibilityConfirmOpen] = useState(false);
  const [pendingIsPublic, setPendingIsPublic] = useState<boolean | null>(null);
  const [learningPointsError, setLearningPointsError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  // Update form data when circle prop changes
  useEffect(() => {
    setFormData({
      name: circle.name,
      description: circle.description,
      isPublic: circle.isPublic,
      logo: circle.logo,
      orgBanner: circle.orgBanner ?? "",
      learningPoints: Array.isArray(circle.learningPoints)
        ? circle.learningPoints.slice(0, CIRCLE_LEARNING_POINT_MAX)
        : [],
    });
    setVisibilityConfirmOpen(false);
    setPendingIsPublic(null);
    setLearningPointsError("");
    setDescriptionError("");
  }, [circle]);

  useEffect(() => {
    if (!anchorSubmitToFooter || !onFooterStateChange) return;
    onFooterStateChange({ isSubmitting });
  }, [anchorSubmitToFooter, isSubmitting, onFooterStateChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLearningPointsError("");
    setDescriptionError("");

    if (!formData.name.trim()) {
      setError("Circle name is required");
      return;
    }

    if (formData.learningPoints.length === 0) {
      setLearningPointsError(LEARNING_POINTS_REQUIRED_MESSAGE);
      return;
    }

    // Don't submit if nothing changed
    const prevBanner = circle.orgBanner ?? "";
    if (
      formData.name === circle.name &&
      formData.description === circle.description &&
      formData.isPublic === circle.isPublic &&
      formData.logo === circle.logo &&
      formData.orgBanner === prevBanner &&
      JSON.stringify(formData.learningPoints) ===
        JSON.stringify(
          Array.isArray(circle.learningPoints)
            ? circle.learningPoints.slice(0, CIRCLE_LEARNING_POINT_MAX)
            : []
        )
    ) {
      if (layout !== "page") {
        onCancel();
      }
      return;
    }

    const descriptionTrimmed = formData.description.trim();
    if (!descriptionTrimmed) {
      setDescriptionError(DESCRIPTION_REQUIRED_MESSAGE);
      return;
    }
    if (descriptionTrimmed.length > CIRCLE_DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(DESCRIPTION_MAX_LENGTH_MESSAGE);
      return;
    }

    setIsSubmitting(true);

    try {
      if (!token || !user?.schoolId) {
        setError("Authentication required. Please log in again.");
        return;
      }

      const bannerChanged = formData.orgBanner !== prevBanner;
      let orgBannerPayload: string | undefined;
      let bannerContentType: string | undefined;
      if (bannerChanged) {
        const b = formData.orgBanner.trim();
        if (b === "") {
          orgBannerPayload = "";
        } else if (formData.orgBanner.startsWith("data:image/")) {
          const processed = await preprocessOrgBannerDataUrl(formData.orgBanner);
          orgBannerPayload = processed.dataUrl;
          bannerContentType = processed.contentType;
        } else {
          orgBannerPayload = formData.orgBanner;
        }
      }

      const response = await api.circles.editSubCircle(
        {
          subcircleId: circle.circleId,
          schoolId: user.schoolId,
          name: formData.name,
          description: descriptionTrimmed,
          isPublic: formData.isPublic,
          logo: formData.logo,
          ...(bannerChanged && orgBannerPayload !== undefined
            ? {
                orgBanner: orgBannerPayload,
                ...(bannerContentType ? { contentType: bannerContentType } : {}),
              }
            : {}),
          learningPoints: formData.learningPoints,
        },
        token
      );

      // Transform API response to match frontend interface
      const updatedCircle: Circle = {
        circleId: response.circle.circleId,
        name: response.circle.name,
        description: response.circle.description,
        isPublic: response.circle.isPublic,
        memberCount: response.circle.memberCount,
        createdAt: response.circle.createdAt,
        createdBy: response.circle.createdBy,
        isSubCircle: response.circle.isSubCircle,
        parentCircle: response.circle.parentCircle,
        circleApproved: response.circle.circleApproved,
        logo: response.circle.logo,
        type: response.circle.type,
        orgBanner:
          typeof response.circle.orgBanner === "string" &&
          response.circle.orgBanner.trim()
            ? response.circle.orgBanner.trim()
            : undefined,
        learningPoints: Array.isArray(response.circle.learningPoints)
          ? response.circle.learningPoints
          : [],
      };

      onCircleUpdated(updatedCircle);
    } catch (error: any) {
      console.error("Error updating subcircle:", error);
      setError(error.message || "Failed to update circle. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBannerCropComplete = useCallback((croppedImageUrl: string) => {
    setFormData((prev) => ({ ...prev, orgBanner: croppedImageUrl }));
    setIsBannerCropperOpen(false);
    setBannerImageToCrop(null);
  }, []);

  const cancelVisibilityChange = useCallback(() => {
    setVisibilityConfirmOpen(false);
    setPendingIsPublic(null);
  }, []);

  const confirmVisibilityChange = useCallback(() => {
    if (pendingIsPublic === null) return;
    setFormData((prev) => ({ ...prev, isPublic: pendingIsPublic }));
    setVisibilityConfirmOpen(false);
    setPendingIsPublic(null);
  }, [pendingIsPublic]);

  const twoColumnModal = anchorSubmitToFooter && layout === "modal";
  const sectionGap = twoColumnModal ? 2 : 3;
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const learningPublicInRightPanel = twoColumnModal && isMdUp;

  const learningPointsField = (
    <FormControl
      fullWidth
      size="medium"
      required
      error={Boolean(learningPointsError)}
    >
      <InputLabel id="edit-learning-points-label">
        Learning points (max {CIRCLE_LEARNING_POINT_MAX})
      </InputLabel>
      <Select
        labelId="edit-learning-points-label"
        multiple
        value={formData.learningPoints}
        onChange={(e) => {
          setLearningPointsError("");
          const next = (e.target.value as string[]).slice(
            0,
            CIRCLE_LEARNING_POINT_MAX
          );
          setFormData((prev) => ({ ...prev, learningPoints: next }));
        }}
        input={
          <OutlinedInput label={`Learning points (max ${CIRCLE_LEARNING_POINT_MAX})`} />
        }
        renderValue={(selected) =>
          (selected as string[])
            .map(
              (id) =>
                CIRCLE_LEARNING_POINT_OPTIONS.find((opt) => opt.id === id)?.label || id
            )
            .join(", ")
        }
        aria-describedby="edit-learning-points-helper"
      >
        {CIRCLE_LEARNING_POINT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.id}
            value={opt.id}
            disabled={
              !formData.learningPoints.includes(opt.id) &&
              formData.learningPoints.length >= CIRCLE_LEARNING_POINT_MAX
            }
          >
            <Checkbox checked={formData.learningPoints.includes(opt.id)} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText id="edit-learning-points-helper">
        {learningPointsError || LEARNING_POINTS_HELPER_TEXT}
      </FormHelperText>
    </FormControl>
  );

  const publicCircleToggle = (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        p: twoColumnModal ? 1.5 : 2,
        bgcolor: lightTheme.colors.mobileNavBackground,
        borderRadius: 2,
        border: `1px solid ${lightTheme.colors.borderLight}`,
      }}
    >
      <Switch
        checked={formData.isPublic}
        onChange={(e) => {
          const next = e.target.checked;
          if (next === formData.isPublic) return;
          setPendingIsPublic(next);
          setVisibilityConfirmOpen(true);
        }}
        color="primary"
        size="medium"
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: theme.palette.common.white,
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
          },
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          fontWeight="600"
          color="text.primary"
          gutterBottom
        >
          Public Circle
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
          Anyone in your organization can join this circle
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
    <Box
      component="form"
      id={EDIT_CIRCLE_FORM_ID}
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        p: 0,
        maxWidth:
          anchorSubmitToFooter || layout === "page"
            ? "100%"
            : 600,
        mx: anchorSubmitToFooter || layout === "page" ? 0 : "auto",
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: twoColumnModal ? "row" : "column" },
          gap: { xs: 0, md: twoColumnModal ? 2 : 0 },
          width: "100%",
          alignItems: "flex-start",
          // Match AddTeamMember two-column form inset so the right-rail title lines up with Invite modal.
          ...(twoColumnModal && { pt: { xs: 1, sm: 1.5 } }),
        }}
      >
        <Box
          sx={{
            flex: { md: twoColumnModal ? 1.2 : "none" },
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: sectionGap,
          }}
        >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: sectionGap,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              py: { xs: 0, sm: 0.5 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
              onClick={() => setIsImageSelectorOpen(true)}
            >
              <Avatar
                src={formData.logo}
                sx={{
                  width: layout === "page" ? 124 : 112,
                  height: layout === "page" ? 124 : 112,
                  border: "3px dashed",
                  borderColor: "divider",
                  bgcolor: lightTheme.colors.mobileNavBackground,
                  fontSize: "2.35rem",
                  fontWeight: "bold",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: lightTheme.colors.materialBlue700,
                    bgcolor: lightTheme.colors.surfaceAliceBlue,
                    boxShadow: `0 4px 12px ${lightTheme.colors.blueTint15}`,
                  },
                }}
              >
                {formData.name.trim()
                  ? formData.name.charAt(0).toUpperCase()
                  : "?"}
              </Avatar>

              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    color: lightTheme.colors.materialBlue700,
                  },
                }}
              >
                {formData.logo ? "Change logo" : "Add logo"}
              </Typography>
            </Box>
          </Box>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            required
            fullWidth
            variant="outlined"
            size="medium"
            sx={{
              flex: 1,
              minWidth: 0,
              alignSelf: { xs: "stretch", sm: "center" },
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: lightTheme.colors.materialBlue700,
                  borderWidth: 1,
                },
              },
              "& .MuiInputLabel-root": {
                fontWeight: 500,
              },
            }}
          />
        </Box>

        {/* Banner image — used on native featured cards when set; inherits main-org banner otherwise */}
        <Box sx={{ width: "100%" }}>
          <input
            ref={bannerFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file || !file.type.startsWith("image/")) return;
              const reader = new FileReader();
              reader.onloadend = () => {
                const url = typeof reader.result === "string" ? reader.result : "";
                if (url) {
                  setBannerImageToCrop(url);
                  setIsBannerCropperOpen(true);
                }
              };
              reader.readAsDataURL(file);
              e.target.value = "";
            }}
          />
          {formData.orgBanner.trim() !== "" ? (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth:
                  layout === "page"
                    ? { xs: "100%", md: 560 }
                    : twoColumnModal
                      ? { xs: "100%", md: "100%" }
                      : { xs: "100%", sm: 320 },
                aspectRatio: BANNER_PREVIEW_ASPECT,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: lightTheme.colors.borderLight,
                mb: 0,
                cursor: "pointer",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                },
              }}
              onClick={() => bannerFileInputRef.current?.click()}
              role="button"
              aria-label="Change banner image"
            >
              <IconButton
                type="button"
                size="small"
                aria-label="Remove banner"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormData((prev) => ({ ...prev, orgBanner: "" }));
                }}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  zIndex: 1,
                  bgcolor: "rgba(255,255,255,0.92)",
                  boxShadow: 1,
                  "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Box
                component="img"
                src={formData.orgBanner}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth:
                  layout === "page"
                    ? { xs: "100%", md: 560 }
                    : twoColumnModal
                      ? { xs: "100%", md: "100%" }
                      : { xs: "100%", sm: 320 },
                aspectRatio: BANNER_PREVIEW_ASPECT,
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "action.hover",
                mb: 0,
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: lightTheme.colors.materialBlue700,
                  bgcolor: lightTheme.colors.surfaceAliceBlue,
                },
              }}
              onClick={() => bannerFileInputRef.current?.click()}
              role="button"
              aria-label="Upload banner image"
            >
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, textAlign: "center" }}>
                Click to upload a banner (optional)
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Input Fields */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: sectionGap }}>
        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => {
            setDescriptionError("");
            setFormData((prev) => ({ ...prev, description: e.target.value }));
          }}
          multiline
          rows={5}
          fullWidth
          required
          variant="outlined"
          size="medium"
          error={Boolean(descriptionError)}
          helperText={
            descriptionError ||
            `${DESCRIPTION_HELPER_TEXT} ${formData.description.length}/${CIRCLE_DESCRIPTION_MAX_LENGTH}`
          }
          inputProps={{ maxLength: CIRCLE_DESCRIPTION_MAX_LENGTH }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: lightTheme.colors.materialBlue700,
                borderWidth: 1,
              },
            },
            "& .MuiInputLabel-root": {
              fontWeight: 500,
            },
          }}
        />

        {!learningPublicInRightPanel && learningPointsField}
      </Box>

      {!learningPublicInRightPanel && publicCircleToggle}

        </Box>

        {twoColumnModal && (
          <Box
            sx={{
              flex: 0.8,
              display: { xs: "none", md: "block" },
              minWidth: 0,
              alignSelf: "stretch",
            }}
          >
            <Box
              sx={{
                pl: 2,
                borderLeft: "1px solid",
                borderColor: "divider",
                position: "sticky",
                top: 8,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
              >
                Make this circle easy to recognize
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Update how this circle looks and who can join—changes apply for everyone
                in your organization who can see it.
              </Typography>

              {learningPublicInRightPanel && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {learningPointsField}
                  {publicCircleToggle}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={visibilityConfirmOpen}
        onClose={cancelVisibilityChange}
        maxWidth="xs"
        fullWidth
        aria-labelledby="circle-visibility-confirm-title"
      >
        <DialogTitle id="circle-visibility-confirm-title">
          {pendingIsPublic === true
            ? "Make this circle public?"
            : "Make this circle private?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {pendingIsPublic === true
              ? "Anyone in your organization will be able to discover and join this circle without a direct invite."
              : "Only people you add or invite will be able to join this circle."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button type="button" variant="text" onClick={cancelVisibilityChange}>
            Cancel
          </Button>
          <NoticeboardButton
            type="button"
            variant="primary"
            onClick={confirmVisibilityChange}
          >
            {pendingIsPublic === true ? "Make public" : "Make private"}
          </NoticeboardButton>
        </DialogActions>
      </Dialog>

      {/* Action Buttons (pinned in ActionModal footer on mobile when anchorSubmitToFooter) */}
      {!anchorSubmitToFooter && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            pt: 2,
            justifyContent: layout === "page" ? "flex-end" : "center",
          }}
        >
          {layout === "page" && (
            <NoticeboardButton
              type="button"
              variant="secondary"
              disabled={isSubmitting}
              onClick={onCancel}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 140 } }}
            >
              Cancel
            </NoticeboardButton>
          )}
          <NoticeboardButton
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            sx={{
              flex: layout === "page" ? { sm: "0 1 280px" } : 1,
              width: layout === "page" ? { xs: "100%", sm: "auto" } : undefined,
            }}
          >
            Update Circle
          </NoticeboardButton>
        </Box>
      )}

      <ProfileImageSelectorModal
        open={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        onImageSelectBase64={(base64) => {
          setFormData((prev) => ({ ...prev, logo: base64 }));
        }}
        returnBase64={true}
        schoolId={user?.schoolId}
        token={token || undefined}
      />
    </Box>

      <ImageCropperModal
        open={isBannerCropperOpen}
        imageSrc={bannerImageToCrop || ""}
        onClose={() => {
          setIsBannerCropperOpen(false);
          setBannerImageToCrop(null);
        }}
        onCropComplete={handleBannerCropComplete}
        aspectRatio={ORGANIZATION_BANNER_ASPECT_RATIO}
      />
    </>
  );
};

export default EditCircleForm;
